// Maximum Band performance entrypoint.
// Keep this file intentionally small so AI assistants can discover the public
// surface without loading the ~224 KB implementation. Search/fetch targeted
// ranges from performanceImpl.ts only when a task actually touches Player code.
import { appStore } from '../core/store';
import {
  MARKER_LEAD_IN_BEAT_OPTIONS,
  MARKER_LEAD_IN_OPTIONS,
  resolveEffectiveLeadIn,
  type SetlistRecord,
  type SongRecord,
} from '../core/types';
import { secondsPerBeat } from '../core/markerFormat';
import '../performanceTransport.css';
import '../spotifyPlayer.css';
import {
  countInLabel,
  getSharedController,
  renderPerformanceSection as renderPerformanceSectionImpl,
} from './performanceImpl';

export { countInLabel, getSharedController, liveCountInLabel } from './performanceImpl';

const CLOCK_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="2"/>
    <path d="M12 7v5l3.4 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;

const METRONOME_ICON = `
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 4h6l3 16H6L9 4Z" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"/>
    <path d="M12 7l2.4 5.2" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
    <path d="M9.3 16h5.4" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
  </svg>`;

interface ActivePlayerContext {
  setlist: SetlistRecord | null;
  song: SongRecord | null;
}

function activePlayerContext(): ActivePlayerContext {
  const state = appStore.getState();
  const setlist = state.setlists.find((candidate) => candidate.id === state.settings.activeSetlistId) ?? null;
  const entry = setlist?.entries[state.settings.activeEntryIndex];
  const song = entry ? state.songs.find((candidate) => candidate.id === entry.songId) ?? null : null;
  return { setlist, song };
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds < 0) return '--:--';
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

function effectiveCountInLabel(setlist: SetlistRecord | null, song: SongRecord | null): string {
  if (!song) return 'Count-in —';
  const effective = resolveEffectiveLeadIn(setlist?.countInOverride ?? null, song);
  if (effective.seconds <= 0) return 'Count-in Off';
  const beatSeconds = effective.beats ? effective.seconds / effective.beats : null;
  return countInLabel({ totalSeconds: effective.seconds, secondsPerBeat: beatSeconds }).replace(' · ', ' ');
}

function songDefaultCountInLabel(song: SongRecord): string {
  if (song.markerLeadInSeconds <= 0) return 'Off';
  const beatSeconds = song.markerLeadInBeats ? song.markerLeadInSeconds / song.markerLeadInBeats : null;
  return countInLabel({ totalSeconds: song.markerLeadInSeconds, secondsPerBeat: beatSeconds }).replace('Count-in · ', '');
}

/**
 * Wrap the large Player implementation with presentation-only enhancements.
 * Playback/audio scheduling stays inside performanceImpl.ts; this wrapper
 * reorganizes existing controls into the compact media-player hierarchy.
 */
export function renderPerformanceSection(container: HTMLElement): () => void {
  const cleanup = renderPerformanceSectionImpl(container);

  const wideSettingsBtn = container.querySelector<HTMLButtonElement>('#click-settings-btn');
  const compactSettingsBtn = container.querySelector<HTMLButtonElement>('#click-settings-btn-compact');
  const wideMenu = container.querySelector<HTMLDivElement>('#click-sound-menu');
  const compactMenu = container.querySelector<HTMLDivElement>('#click-sound-menu-compact');
  const compactLoopBtn = container.querySelector<HTMLButtonElement>('#loop-btn-compact');
  const compactLoopLabel = container.querySelector<HTMLElement>('#loop-mode-label-compact');
  const wideLoopLabel = container.querySelector<HTMLElement>('#loop-mode-label');
  const currentTitle = container.querySelector<HTMLElement>('#current-title');
  const currentHeading = container.querySelector<HTMLElement>('.current-song-heading');
  const setlistMeta = container.querySelector<HTMLElement>('#setlist-panel-meta');
  const wideTransportRow = container.querySelector<HTMLElement>('#transport-cluster');
  const wideTransportLeft = container.querySelector<HTMLElement>('#transport-cluster-left');
  const wideTransportCore = container.querySelector<HTMLElement>('#transport-cluster-core');
  const wideTransportRight = container.querySelector<HTMLElement>('#transport-cluster-right');
  const widePreviousBtn = container.querySelector<HTMLButtonElement>('#prev-song-btn');
  const widePlayBtn = container.querySelector<HTMLButtonElement>('#play-pause-btn');
  const wideLoopBtn = container.querySelector<HTMLButtonElement>('#loop-btn');
  const wideNextBtn = container.querySelector<HTMLButtonElement>('#next-song-btn');

  // The top of the Player is the one source of truth for "what am I playing?"
  // rather than wasting vertical space repeating the song in a second card.
  let topCopy = currentHeading?.querySelector<HTMLElement>('.spotify-player-top-copy') ?? null;
  let topMeta = currentHeading?.querySelector<HTMLElement>('.spotify-player-top-meta') ?? null;
  if (currentHeading && currentTitle && !topCopy) {
    topCopy = document.createElement('div');
    topCopy.className = 'spotify-player-top-copy';
    currentTitle.before(topCopy);
    topCopy.appendChild(currentTitle);
    topMeta = document.createElement('div');
    topMeta.className = 'spotify-player-top-meta';
    topCopy.appendChild(topMeta);
  }

  const refreshTopContext = (): void => {
    if (!topMeta) return;
    const state = appStore.getState();
    const { setlist, song } = activePlayerContext();
    if (!song) {
      topMeta.textContent = setlist?.name ?? 'No setlist selected';
      return;
    }

    const position = setlist ? `${state.settings.activeEntryIndex + 1} of ${setlist.entries.length}` : '';
    const existingRouting = setlistMeta?.textContent?.split(' · ').slice(1).join(' · ').trim() ?? '';
    const routing = existingRouting
      .replace('Tracks: ', '')
      .replace(', Click: ', ' · Click: ');
    topMeta.textContent = [setlist?.name, position, formatDuration(song.durationSeconds), routing]
      .filter(Boolean)
      .join(' · ');
  };
  refreshTopContext();

  // Reuse the implementation's precisely audio-scheduled beat flashes, but
  // collect all four onto the primary Play/Pause control. The implementation
  // still owns when each span becomes active, so this adds no parallel timer.
  const compactPlayBtn = container.querySelector<HTMLButtonElement>('#play-pause-btn-compact');
  const compactBeatFlashes = Array.from(container.querySelectorAll<HTMLSpanElement>('#transport-row-compact .btn-transport-beat-flash'));
  if (compactPlayBtn) compactBeatFlashes.forEach((flash) => compactPlayBtn.appendChild(flash));

  const ensureCountInStatus = (parent: HTMLElement | null, id: string): HTMLButtonElement | null => {
    if (!parent) return null;
    let status = container.querySelector<HTMLButtonElement>(`#${id}`);
    if (!status) {
      status = document.createElement('button');
      status.type = 'button';
      status.id = id;
      status.className = 'player-count-in-status';
      status.title = 'Count-in settings';
      status.setAttribute('aria-label', 'Open count-in settings');
      const settingsBtn = container.querySelector<HTMLButtonElement>('#click-settings-btn');
      status.addEventListener('click', () => settingsBtn?.click());
      parent.appendChild(status);
    }
    return status;
  };

  const wideCountInStatus = ensureCountInStatus(wideTransportLeft, 'player-count-in-status');
  const compactCountInStatus = ensureCountInStatus(compactSettingsBtn, 'player-count-in-status-compact');
  let wideClickStatus = container.querySelector<HTMLButtonElement>('#click-status-btn');
  if (!wideClickStatus && wideTransportRight) {
    wideClickStatus = document.createElement('button');
    wideClickStatus.type = 'button';
    wideClickStatus.id = 'click-status-btn';
    wideClickStatus.className = 'player-transport-utility player-click-status-btn';
    wideClickStatus.addEventListener('click', () => wideSettingsBtn?.click());
    wideTransportRight.appendChild(wideClickStatus);
  }
  wideClickStatus?.classList.add('player-transport-utility', 'player-click-status-btn');

  if (wideTransportRow && wideTransportLeft && wideTransportCore && wideTransportRight && wideCountInStatus && wideLoopBtn && widePreviousBtn && widePlayBtn && wideNextBtn && wideClickStatus && wideSettingsBtn) {
    wideTransportRow.classList.add('player-transport-utilities-ready');
    wideCountInStatus.classList.add('player-transport-utility', 'player-count-in-utility');
    wideLoopBtn.classList.add('player-transport-utility', 'player-loop-utility');
    widePreviousBtn.classList.add('player-transport-core-secondary');
    wideNextBtn.classList.add('player-transport-core-secondary');
    wideSettingsBtn.classList.add('player-transport-utility', 'player-more-utility');
    wideTransportLeft.append(wideCountInStatus, wideLoopBtn);
    wideTransportCore.append(widePreviousBtn, widePlayBtn, wideNextBtn);
    wideTransportRight.append(wideClickStatus, wideSettingsBtn);
  }

  const proxyWideUtilityToCompactDrawer = (event: Event, compactSelector: string): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    requestAnimationFrame(() => container.querySelector<HTMLButtonElement>(compactSelector)?.click());
  };

  const handleWideCountInDrawer = (event: Event): void => proxyWideUtilityToCompactDrawer(event, '#player-count-in-status-compact');
  const handleWideLoopDrawer = (event: Event): void => proxyWideUtilityToCompactDrawer(event, '#loop-btn-compact');
  const handleWideClickDrawer = (event: Event): void => proxyWideUtilityToCompactDrawer(event, '#click-status-btn-compact');
  const handleWideOptionsDrawer = (event: Event): void => proxyWideUtilityToCompactDrawer(event, '#click-settings-btn-compact');

  wideCountInStatus?.addEventListener('click', handleWideCountInDrawer, true);
  wideLoopBtn?.addEventListener('click', handleWideLoopDrawer, true);
  wideClickStatus?.addEventListener('click', handleWideClickDrawer, true);
  wideSettingsBtn?.addEventListener('click', handleWideOptionsDrawer, true);

  const buildCountInMenuSection = (): HTMLElement => {
    const { setlist, song } = activePlayerContext();
    const section = document.createElement('div');
    section.className = 'player-header-menu-section player-count-in-menu-section';

    if (!song) {
      section.innerHTML = '<p class="player-count-in-menu-note">No song loaded.</p>';
      return section;
    }

    const override = setlist?.countInOverride ?? null;
    const hasKnownBpm = song.mode === 'metronome' || song.syntheticClickEnabled;
    const options: string[] = [];

    if (setlist) {
      options.push(`<button type="button" class="player-count-in-option${override === null ? ' is-selected' : ''}" data-player-count-in="song">Song</button>`);
    }
    options.push(`<button type="button" class="player-count-in-option${override === 'off' || (!setlist && song.markerLeadInSeconds <= 0) ? ' is-selected' : ''}" data-player-count-in="off">Off</button>`);

    if (hasKnownBpm) {
      for (const beats of MARKER_LEAD_IN_BEAT_OPTIONS) {
        const selected = setlist
          ? override !== null && typeof override === 'object' && override.beats === beats
          : song.markerLeadInBeats === beats;
        options.push(`<button type="button" class="player-count-in-option${selected ? ' is-selected' : ''}" data-player-count-in="beats" data-value="${beats}">${beats}ct</button>`);
      }
    } else {
      for (const seconds of MARKER_LEAD_IN_OPTIONS) {
        const selected = setlist
          ? override !== null && typeof override === 'object' && override.beats === null && override.seconds === seconds
          : song.markerLeadInBeats === null && song.markerLeadInSeconds === seconds;
        options.push(`<button type="button" class="player-count-in-option${selected ? ' is-selected' : ''}" data-player-count-in="seconds" data-value="${seconds}">${seconds}s</button>`);
      }
    }

    section.innerHTML = `
      <div class="player-count-in-options">${options.join('')}</div>
      ${setlist ? `<p class="player-count-in-menu-note">Song default: ${songDefaultCountInLabel(song)}</p>` : ''}
    `;
    return section;
  };

  const loopModeText = (): string => compactLoopLabel?.textContent?.trim() || wideLoopLabel?.textContent?.trim() || 'Off';

  const buildCompactControlSummary = (): HTMLElement => {
    const { setlist, song } = activePlayerContext();
    const wrap = document.createElement('div');
    wrap.className = 'spotify-player-menu-summary';

    const loop = document.createElement('button');
    loop.type = 'button';
    loop.className = 'spotify-player-menu-row';
    loop.dataset.playerLoopCycle = 'true';
    loop.innerHTML = `<span class="spotify-player-menu-icon">↻</span><span>Loop</span><strong>${loopModeText()}</strong><span class="spotify-player-menu-chevron">›</span>`;
    wrap.appendChild(loop);

    const countDetails = document.createElement('details');
    countDetails.className = 'spotify-player-menu-details';
    const countSummary = document.createElement('summary');
    countSummary.className = 'spotify-player-menu-row';
    countSummary.innerHTML = `<span class="spotify-player-menu-icon">◷</span><span>Count-in</span><strong>${effectiveCountInLabel(setlist, song).replace('Count-in ', '')}</strong><span class="spotify-player-menu-chevron">›</span>`;
    countDetails.appendChild(countSummary);
    countDetails.appendChild(buildCountInMenuSection());
    wrap.appendChild(countDetails);

    const more = document.createElement('div');
    more.className = 'spotify-player-menu-more';
    more.innerHTML = '<span class="spotify-player-menu-icon">⚙</span><span>More options</span>';
    wrap.appendChild(more);

    return wrap;
  };

  const ensureMenuExtras = (menu: HTMLDivElement | null, isCompact: boolean, force = false): void => {
    if (!menu) return;
    const existing = menu.querySelector<HTMLElement>('.player-transport-menu-extras');
    if (existing && !force) return;
    existing?.remove();

    const extras = document.createElement('div');
    extras.className = 'player-transport-menu-extras';

    if (isCompact) {
      extras.appendChild(buildCompactControlSummary());
    } else {
      extras.appendChild(buildCountInMenuSection());
      const beatStripHeader = container.querySelector<HTMLElement>('#beat-strip-header');
      if (beatStripHeader?.classList.contains('has-click')) {
        const visualToggle = document.createElement('button');
        visualToggle.type = 'button';
        visualToggle.dataset.action = 'toggle-click-widget-collapsed';
        visualToggle.textContent = beatStripHeader.classList.contains('is-collapsed') ? 'Show visual click' : 'Hide visual click';
        extras.appendChild(visualToggle);
      }
    }

    menu.prepend(extras);
  };

  const refreshTransportExtras = (forceMenus = false): void => {
    const { setlist, song } = activePlayerContext();
    const label = effectiveCountInLabel(setlist, song);
    for (const status of [wideCountInStatus, compactCountInStatus]) {
      if (!status) continue;
      if (status === wideCountInStatus) {
        status.innerHTML = `<span class="player-utility-icon">${CLOCK_ICON}</span>`;
        status.classList.toggle('is-active', song ? resolveEffectiveLeadIn(setlist?.countInOverride ?? null, song).seconds > 0 : false);
      } else {
        status.textContent = label;
      }
      status.disabled = !song;
    }
    if (wideClickStatus) {
      const hasClick = song != null && (song.mode === 'metronome' || song.syntheticClickEnabled);
      wideClickStatus.classList.toggle('is-active', hasClick);
      wideClickStatus.innerHTML = `<span class="player-utility-icon">${METRONOME_ICON}</span>`;
      wideClickStatus.title = hasClick ? 'Click on' : 'Click off';
      wideClickStatus.setAttribute('aria-label', `${wideClickStatus.title}. Open click settings`);
    }
    refreshTopContext();
    ensureMenuExtras(wideMenu, false, forceMenus);
    ensureMenuExtras(compactMenu, true, forceMenus);
  };

  refreshTransportExtras();

  const menuObserver = new MutationObserver(() => {
    ensureMenuExtras(wideMenu, false);
    ensureMenuExtras(compactMenu, true);
  });
  if (wideMenu) menuObserver.observe(wideMenu, { childList: true });
  if (compactMenu) menuObserver.observe(compactMenu, { childList: true });

  const loopObserver = new MutationObserver(() => ensureMenuExtras(compactMenu, true, true));
  if (compactLoopLabel) loopObserver.observe(compactLoopLabel, { childList: true, characterData: true, subtree: true });

  const metaObserver = new MutationObserver(refreshTopContext);
  if (setlistMeta) metaObserver.observe(setlistMeta, { childList: true, characterData: true, subtree: true });

  const unsubscribeStore = appStore.subscribe(() => refreshTransportExtras(true));

  const handleTransportMenuSetting = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const loopCycle = target.closest<HTMLElement>('[data-player-loop-cycle]');
    if (loopCycle && container.contains(loopCycle)) {
      compactLoopBtn?.click();
      queueMicrotask(() => ensureMenuExtras(compactMenu, true, true));
      return;
    }

    const option = target.closest<HTMLButtonElement>('[data-player-count-in]');
    if (!option || !container.contains(option)) return;

    const { setlist, song } = activePlayerContext();
    if (!song) return;
    const action = option.dataset.playerCountIn;
    const value = Number(option.dataset.value);

    void (async () => {
      if (setlist) {
        if (action === 'song') {
          await appStore.setSetlistCountInOverride(setlist.id, null);
        } else if (action === 'off') {
          await appStore.setSetlistCountInOverride(setlist.id, 'off');
        } else if (action === 'beats' && Number.isFinite(value)) {
          await appStore.setSetlistCountInOverride(setlist.id, {
            seconds: value * secondsPerBeat(song.metronomeSettings),
            beats: value,
          });
        } else if (action === 'seconds' && Number.isFinite(value)) {
          await appStore.setSetlistCountInOverride(setlist.id, { seconds: value, beats: null });
        }
      } else if (action === 'off') {
        await appStore.updateSong(song.id, { markerLeadInSeconds: 0, markerLeadInBeats: null });
      } else if (action === 'beats' && Number.isFinite(value)) {
        await appStore.updateSong(song.id, {
          markerLeadInSeconds: value * secondsPerBeat(song.metronomeSettings),
          markerLeadInBeats: value,
        });
      } else if (action === 'seconds' && Number.isFinite(value)) {
        await appStore.updateSong(song.id, { markerLeadInSeconds: value, markerLeadInBeats: null });
      }
    })();
  };
  container.addEventListener('click', handleTransportMenuSetting);

  const startArmedSectionLoop = (event: Event): boolean => {
    const target = event.target;
    if (!(target instanceof Element)) return false;

    const playButton = target.closest<HTMLButtonElement>('#play-pause-btn, #play-pause-btn-compact');
    if (!playButton || !container.contains(playButton)) return false;

    const controller = getSharedController();
    const status = controller?.getState().status;
    if (!controller || status === 'PLAYING' || status === 'PRE_ROLLING') return false;

    const loopLabel = container.querySelector<HTMLElement>('#loop-mode-label');
    if (loopLabel?.textContent?.trim() !== 'Section') return false;
    if (container.querySelector('.setlist-marker-row.is-loop-target')) return false;

    const currentSection = container.querySelector<HTMLElement>('.setlist-marker-row.is-selected, .setlist-marker-row.is-active');
    if (!currentSection) return false;

    event.preventDefault();
    event.stopImmediatePropagation();
    currentSection.click();
    return true;
  };

  const handlePointerUpCapture = (event: PointerEvent): void => {
    if (event.button !== 0) return;
    startArmedSectionLoop(event);
  };

  const handleKeydownCapture = (event: KeyboardEvent): void => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    startArmedSectionLoop(event);
  };

  container.addEventListener('pointerup', handlePointerUpCapture, true);
  container.addEventListener('keydown', handleKeydownCapture, true);

  return () => {
    menuObserver.disconnect();
    loopObserver.disconnect();
    metaObserver.disconnect();
    unsubscribeStore();
    container.removeEventListener('click', handleTransportMenuSetting);
    container.removeEventListener('pointerup', handlePointerUpCapture, true);
    container.removeEventListener('keydown', handleKeydownCapture, true);
    wideCountInStatus?.removeEventListener('click', handleWideCountInDrawer, true);
    wideLoopBtn?.removeEventListener('click', handleWideLoopDrawer, true);
    wideClickStatus?.removeEventListener('click', handleWideClickDrawer, true);
    wideSettingsBtn?.removeEventListener('click', handleWideOptionsDrawer, true);
    cleanup();
  };
}
